import type { ReactNode } from "react"
import { useEffect, useRef } from "react"

import { Popover } from "@productlane/ui"
import {
	Command,
	CommandGroup,
	CommandItem,
	CommandList,
} from "@productlane/ui/src/components/internal/command"

const CLOSE_DELAY_MS = 200

/**
 * A row inside a Command list that opens a side menu on hover or Enter, like
 * Linear's nested pickers. The delay lets the pointer cross the gap to the menu.
 */
export function CommandSubmenu({
	value,
	trigger,
	isOpen,
	onOpenChange,
	children,
	className,
}: {
	value: string
	trigger: ReactNode
	isOpen: boolean
	onOpenChange: (open: boolean) => void
	children: ReactNode
	className?: string
}) {
	const timeoutRef = useRef<NodeJS.Timeout | null>(null)

	const cancelClose = () => {
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current)
			timeoutRef.current = null
		}
	}
	const open = () => {
		cancelClose()
		onOpenChange(true)
	}
	const closeSoon = () => {
		timeoutRef.current = setTimeout(() => {
			if (isOpen) onOpenChange(false)
		}, CLOSE_DELAY_MS)
	}

	useEffect(() => {
		if (!isOpen) cancelClose()
	}, [isOpen])
	useEffect(() => cancelClose, [])

	return (
		<Popover
			open={isOpen}
			onOpenChange={onOpenChange}
			side="right"
			align="start"
			className="min-w-[172px] p-0"
			modal={false}
			content={
				<div onMouseEnter={open} onMouseLeave={closeSoon}>
					<Command shouldFilter={false}>
						<CommandList>
							<CommandGroup>{children}</CommandGroup>
						</CommandList>
					</Command>
				</div>
			}
		>
			<div onMouseEnter={open} onMouseLeave={closeSoon} className="relative">
				<CommandItem value={value} onSelect={open} className={className}>
					<div className="flex w-full items-center gap-2">
						<div className="flex min-w-0 flex-1 items-center gap-2">
							{trigger}
						</div>
						{/* Linear marks a side menu with a small solid caret. */}
						<span className="h-0 w-0 flex-none border-y-[3.5px] border-l-[4px] border-y-transparent border-l-gray-400 dark:border-l-gray-500" />
					</div>
				</CommandItem>
			</div>
		</Popover>
	)
}
